import { productGroups, createCheckoutProduct } from '../config/storefrontProducts';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, CreditCard, Users, ChevronDown, ArrowUpRight, Check, KeyRound, Monitor, RefreshCw, ShoppingBag, Ticket, RotateCcw, MessageCircle, Mail, ArrowRight, ArrowDownRight, Sparkles, CircleHelp, Megaphone, ReceiptText, Smartphone } from 'lucide-react';
import './Home.css';
import openaiLogo from '../assets/brands/openai.svg';
import claudeLogo from '../assets/brands/claude.svg';
import geminiLogo from '../assets/brands/gemini.svg';
import grokLogo from '../assets/brands/grok.svg';

/**
 * 首页 - ChatGPT Plus 充值服务
 */
function Home() {
  const navigate = useNavigate();
  const [openFaqIndex, setOpenFaqIndex] = useState(null);


  // 三个独立的购买模块：代充值、成品号、网页共享版。
  // 价格与产品 ID 集中在这里，后续调整价格或 SKU 时不需要改 JSX。


  const heroBrands = [
    { id: 'gpt', name: 'ChatGPT', logo: openaiLogo },
    { id: 'claude', name: 'Claude', logo: claudeLogo },
    { id: 'gemini', name: 'Gemini', logo: geminiLogo },
    { id: 'grok', name: 'Grok', logo: grokLogo },
  ];

  const purchaseSteps = [
    { number: '01', Icon: ShoppingBag, symbol: CreditCard, title: '选产品 · 下单', description: '选好套餐，填写邮箱并完成付款。', detail: '套餐 / 邮箱 / 支付' },
    { number: '02', Icon: Ticket, symbol: KeyRound, title: '收卡密 · 看订单', description: '支付成功页和订单详情页查看交付内容。', detail: '卡密 / 账号 / 网页入口' },
    { number: '03', Icon: Monitor, symbol: Check, title: '按步骤 · 开始用', description: '按订单指引激活订阅，或打开网页入口。', detail: '激活 / 登录 / 使用' },
  ];

  const handleBuyClick = (product) => {
    if (product.available === false) return;
    navigate(`/order/confirm?productId=${product.productId}`, { state: { product } });
  };

  const faqData = [
    {
      question: '购买后怎么收到交付内容？',
      answer: '付款成功后，卡密、成品账号或网页访问入口会自动显示在支付成功页面和订单详情页；如果没看到，请联系在线客服核对订单。'
    },
    {
      question: '没有海外信用卡可以充值吗？支持哪些支付方式？',
      answer: '完全可以！我们支持支付宝和信用卡支付，无需海外信用卡，使用国内支付宝即可轻松完成充值。'
    },
    {
      question: '什么账号可以充值？',
      answer: '只要是正常的 ChatGPT 账号都可以充值，包括免费账号和已过期的 Plus 账号。我们会为您的账号升级到 Plus 或 Pro 版本。'
    },
    {
      question: '购买后没收到卡密或网页入口怎么办？',
      answer: '先刷新支付成功页和订单详情页。如果仍然没有看到，请直接联系右下角在线客服，提供订单号，我们会帮你核对并补发。'
    },
    {
      question: '购买后怎么充值？',
      answer: '代充值按页面步骤激活，成品号登录即可使用，网页共享版点击专属入口即可开始使用。GPT + Claude 组合版可以在同一个网页入口切换两个模型。'
    }
  ];

  return (
    <div className="home-page min-h-screen bg-[var(--site-canvas)]">
      {/* 多品牌视觉以大师兄为中心，购买与咨询入口分别可达。 */}
      <section className="home-hero" aria-labelledby="hero-heading">
        <div className="hero-composition max-w-6xl mx-auto">
          <div className="hero-copy">
            <div className="hero-kicker"><Sparkles size={16} aria-hidden="true" /><span>AI 订阅 / 广告投放 / 更多代充</span><span className="hero-kicker-rule" /></div>
            <h1 id="hero-heading">AI 代充，<br /><span>找大师兄<svg viewBox="0 0 280 16" aria-hidden="true"><path d="M4 9 Q140 -2 276 8 M20 14 Q145 5 250 13" /></svg></span><span className="hero-title-star" aria-hidden="true">✳</span></h1>
            <p className="hero-subtitle">从 AI 到广告，一切代充找大师兄。</p>
            <div className="hero-service-types" aria-label="服务类型">
              <span><RefreshCw size={16} aria-hidden="true" />代充值</span>
              <span><KeyRound size={16} aria-hidden="true" />成品号</span>
              <span><Monitor size={16} aria-hidden="true" />网页共享</span>
            </div>

            <div className="hero-offer">
              <div className="hero-offer-label"><img src={openaiLogo} alt="" width="24" height="24" /><div><strong>ChatGPT Plus</strong><span>代充值 · 1 个月</span></div></div>
              <div className="hero-offer-price"><span>¥</span><strong>139</strong><span>/ 月</span></div>
            </div>
            <div className="hero-actions">
              <a href="#products" className="brand-purchase home-hero-buy">立即充值<ArrowRight size={24} aria-hidden="true" /></a>
              <p className="hero-consult-note">广告及其他产品代充，请联系右下角在线客服。</p>
            </div>
            <div className="hero-invoice">
              <span className="hero-invoice-icon"><ReceiptText size={22} strokeWidth={1.5} aria-hidden="true" /></span>
              <div><strong>需要 Invoice？<span>免费领取</span></strong><p>联系右下角在线客服即可领取。</p></div>
              <ArrowDownRight size={20} aria-hidden="true" />
            </div>
          </div>

          <div className="hero-universe" role="group" aria-label="AI、广告与其他充值入口">
            <div className="universe-grid" aria-hidden="true" />
            <div className="universe-ring universe-ring-outer" aria-hidden="true" />
            <div className="universe-ring universe-ring-inner" aria-hidden="true" />
            <svg className="universe-connections" viewBox="0 0 520 520" aria-hidden="true">
              <path d="M260 242 Q180 155 115 100 M260 242 Q340 155 405 100 M260 242 Q185 300 75 325 M260 242 Q340 300 445 325 M260 242 L260 465" />
              <circle cx="260" cy="80" r="4" /><circle cx="465" cy="210" r="4" /><circle cx="55" cy="210" r="4" />
            </svg>
            <span className="universe-spark universe-spark-one" aria-hidden="true">✳</span>
            <span className="universe-spark universe-spark-two" aria-hidden="true">+</span>
            <div className="universe-hub"><img className="universe-monkey" src="/images/logo.jpg" alt="AI 大师兄 Logo" width="68" height="68" /><strong>大师兄</strong><span>一切代充</span></div>
            {heroBrands.map((brand) => {
              const contents = <><span className="universe-brand-icon"><img src={brand.logo} alt="" width="46" height="46" /></span><strong>{brand.name}</strong><span className="universe-brand-caption">{brand.id === 'gpt' ? '查看套餐' : '充值咨询'}<ArrowUpRight size={13} aria-hidden="true" /></span></>;
              const className = `universe-brand universe-brand-${brand.id}`;
              return brand.id === 'gpt'
                ? <a key={brand.id} href="#products" className={className} aria-label="ChatGPT 充值套餐">{contents}</a>
                : <Link key={brand.id} to="/customer-service" className={className} aria-label={`${brand.name} 充值咨询`}>{contents}</Link>;
            })}
            <Link to="/customer-service" className="universe-brand universe-brand-more" aria-label="广告及其他产品代充咨询">
              <span className="universe-more-icon"><Megaphone size={28} strokeWidth={1.6} aria-hidden="true" /></span>
              <span><strong>一切代充</strong><span className="universe-brand-caption">广告投放 · 更多产品<ArrowUpRight size={13} aria-hidden="true" /></span></span>
            </Link>
            <span className="universe-caption"><span />不止 AI，你的订阅与充值助手。</span>
          </div>
        </div>
        <div className="hero-assurances max-w-6xl mx-auto">
          <span><Shield size={20} aria-hidden="true" /><strong>充值失败退款</strong></span>
          <span><CreditCard size={20} aria-hidden="true" /><strong>支付宝支付</strong></span>
          <span><Users size={20} aria-hidden="true" /><strong>真人客服</strong><small>9:00—23:00</small></span>
        </div>
      </section>

      <section className="home-journey" aria-labelledby="journey-title">
        <div className="max-w-6xl mx-auto">
          <div className="journey-heading"><div><p className="catalog-eyebrow">简单三步</p><h2 id="journey-title">从下单，到用上 AI。</h2></div><span className="journey-heading-symbol" aria-hidden="true"><ShoppingBag /><ArrowRight /><Sparkles /></span></div>
          <ol className="journey-steps">
            {purchaseSteps.map((step) => (
              <li key={step.number} className="journey-step">
                <div className="journey-illustration" aria-hidden="true">
                  <span className="journey-number">{step.number}</span>
                  <div className="journey-icon-tile"><step.Icon size={40} strokeWidth={1.4} /><span><step.symbol size={18} /></span></div>
                  <span className="journey-dots">· · ·</span>
                </div>
                <h3>{step.title}</h3><p>{step.description}</p><span className="journey-detail">{step.detail}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* 颜色标识购买方式，同类产品使用相同的视觉层级。 */}
      <section id="products" className="product-catalog px-4 py-16 md:py-20" aria-labelledby="catalog-title">
        <div className="max-w-6xl mx-auto">
          <div className="catalog-heading">
            <div>
              <p className="catalog-eyebrow">大师兄 · AI 服务</p>
              <h2 id="catalog-title">先选使用方式，再选产品</h2>
            </div>
            <p className="catalog-intro">用自己的账号、买一个新账号，或直接用网页版。<br className="hidden md:block" />三种方式，交付内容一目了然。</p>
          </div>

          <nav className="catalog-guide" aria-label="按使用方式选择产品">
            {productGroups.map((group) => (
              <a key={group.id} href={`#product-${group.id}`} className={`catalog-choice catalog-theme-${group.id}`}>
                <group.Icon size={20} aria-hidden="true" />
                <div><strong>{group.title}</strong><span>{group.audience}</span></div>
                <ArrowUpRight size={18} aria-hidden="true" />
              </a>
            ))}
          </nav>

          <div className="catalog-sections">
            {productGroups.map((group) => (
              <section key={group.id} id={`product-${group.id}`} className={`catalog-section catalog-theme-${group.id}`} aria-labelledby={`heading-${group.id}`}>
                <div className="catalog-section-heading">
                  <div className="catalog-section-title">
                    <span className="catalog-number" aria-hidden="true">{group.number}</span>
                    <div>
                      <h3 id={`heading-${group.id}`}>{group.title}</h3>
                      <p>{group.description}</p>
                    </div>
                  </div>
                  <span className="catalog-delivery"><group.Icon size={16} aria-hidden="true" />{group.delivery}</span>
                </div>

                <div className={`catalog-product-grid ${group.products.length === 3 ? 'catalog-product-grid-three' : ''}`}>
                  {group.products.map((product) => {
                    const accountFeeRMB = product.accountFeeRMB || 0;
                    const totalPriceRMB = product.priceRMB + accountFeeRMB;
                    const buyProduct = createCheckoutProduct(product, group);
                    return (
                      <article key={product.id} className="catalog-card">
                        <div className="catalog-card-heading">
                          <div className="catalog-brand-row">
                            <span className="catalog-product-type">{group.title}</span>
                            <div className="catalog-brand-marks" aria-hidden="true">
                              <span className="catalog-brand-mark"><img src={openaiLogo} alt="" width="26" height="26" /></span>
                              {product.includesClaude && <span className="catalog-brand-mark catalog-brand-mark-claude"><img src={claudeLogo} alt="" width="26" height="26" /></span>}
                            </div>
                          </div>
                          <h4>{product.name}{product.variant && product.variant !== '网页共享' && <span className="catalog-version">{product.variant}</span>}</h4>
                          <p>{product.tagline}</p>
                        </div>
                        <div className="catalog-card-body">
                          <div className="catalog-price">
                            <span>¥</span><strong>{product.priceRMB}</strong>
                            {accountFeeRMB > 0 ? <span className="catalog-account-fee">+ {accountFeeRMB} 元</span> : <span>/ 月</span>}
                          </div>
                          {accountFeeRMB > 0 && (
                            <p className="catalog-price-breakdown">1 个月订阅 + {accountFeeRMB} 元账号费 · 合计 <strong>¥{totalPriceRMB}</strong></p>
                          )}
                          <p className="catalog-description">{product.description}</p>
                          {product.fulfillment === 'account' && (
                            <section className="catalog-account-notice" aria-label="手机号与验证说明">
                              <h5><Smartphone size={17} aria-hidden="true" />手机号与验证说明</h5>
                              <dl>
                                <div>
                                  <dt>默认发货</dt>
                                  <dd>账号未绑定手机号。如出现手机号验证，需自行绑定手机号完成验证。</dd>
                                </div>
                                <div>
                                  <dt>可换账号</dt>
                                  <dd>可<Link to="/customer-service">联系客服</Link>更换为已绑定一次性手机号的账号，大概率不会再次触发手机号验证。<strong>一次性手机号不支持二次或多次验证。</strong></dd>
                                </div>
                              </dl>
                            </section>
                          )}
                          <ul className="catalog-features" aria-label="产品特点">
                            {product.tags.map((tag) => <li key={tag}><Check size={15} aria-hidden="true" />{tag}</li>)}
                          </ul>
                          <button onClick={() => handleBuyClick(buyProduct)} disabled={product.available === false} className="catalog-buy brand-purchase" aria-label={`${product.available === false ? '暂时缺货' : '购买'}：${group.title} ${product.name} ${product.variant}`}>
                            {product.available === false ? '暂时缺货' : '购买'}
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </div>
      </section>

      {/* 服务保障 */}
      <section className="container mx-auto px-4 py-12 md:py-16">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs font-bold text-[var(--site-brand)] tracking-[0.3em] text-center mb-3">GUARANTEE</p>
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-3 text-[var(--site-ink)]">万一出问题，怎么办？</h2>
          <p className="text-sm text-[var(--site-muted)] text-center mb-12 max-w-2xl mx-auto leading-relaxed">
            售后有规则，遇事有人管。
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            <div className="border border-[var(--site-border)] rounded-2xl p-6 sm:p-8 home-service-card bg-white">
              <div className="guarantee-emblem" aria-hidden="true"><RotateCcw size={56} strokeWidth={1.2} /><strong>100<span>%</span></strong></div>
              <h3 className="text-xl font-bold text-[var(--site-ink)] mb-3">充值失败 100% 退款</h3>
              <p className="text-sm text-[var(--site-muted)] leading-relaxed">
                充值未能成功，全额原路退回，不扣手续费。
              </p>
            </div>

            <div className="border border-[var(--site-border)] rounded-2xl p-6 sm:p-8 home-service-card bg-white">
              <div className="guarantee-emblem" aria-hidden="true"><Shield size={56} strokeWidth={1.2} /><strong>30<span>天</span></strong></div>
              <h3 className="text-xl font-bold text-[var(--site-ink)] mb-3">30 天质保 · 掉订阅按天退款</h3>
              <p className="text-sm text-[var(--site-muted)] leading-relaxed mb-3">
                从充值成功之日起 30 天内，Plus 订阅若意外掉单，按剩余天数原路退款。
              </p>
              <p className="text-xs text-[var(--site-muted)] leading-relaxed">
                * 掉单指 Plus 订阅意外失效，账号回退到 Free 状态。
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* FAQ 常见问题 */}
      <section className="bg-[var(--site-section)] py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="section-symbol" aria-hidden="true"><CircleHelp size={30} strokeWidth={1.5} /></div>
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-2 text-[var(--site-ink)]">常见问题解答</h2>
            <p className="text-sm text-[var(--site-muted)] text-center mb-8 md:mb-12">购买、交付、使用，常见问题在这里。</p>

            <div className="space-y-4">
              {faqData.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white border border-[var(--site-border)] rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-lg"
                >
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left"
                  >
                    <span className="text-base font-semibold text-[var(--site-ink)] pr-4">{faq.question}</span>
                    <ChevronDown
                      className={`w-5 h-5 text-[var(--site-muted)] flex-shrink-0 transition-transform duration-300 ${
                        openFaqIndex === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {openFaqIndex === index && (
                    <div className="px-6 pb-5 pt-0">
                      <p className="text-sm text-[var(--site-muted)] leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 联系客服方式 */}
      <section className="container mx-auto px-4 py-12 md:py-16 pb-20">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs font-bold text-[var(--site-brand)] tracking-[0.3em] text-center mb-3">SUPPORT</p>
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-3 text-[var(--site-ink)]">如何联系客服</h2>
          <p className="text-sm text-[var(--site-muted)] text-center mb-12 max-w-2xl mx-auto leading-relaxed">
            急事找在线客服，非紧急问题也可发邮件。
          </p>

          <div className="flex flex-col md:flex-row gap-6">

            {/* 在线客服 */}
            <div className="flex-1 border border-[var(--site-border)] rounded-2xl p-6 sm:p-8 home-service-card bg-white relative overflow-hidden">
              <div className="home-service-icon support-symbol"><MessageCircle size={22} aria-hidden="true" /></div>
              <h3 className="text-xl font-bold text-[var(--site-ink)] mb-3">在线客服气泡</h3>
              <p className="text-sm text-[var(--site-muted)] leading-relaxed mb-6">
                代充咨询、订单问题，点击右下角客服气泡即可联系。需要 Invoice，也可向客服免费领取。
              </p>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-[var(--site-brand)] rounded-full flex-shrink-0"></span>
                <span className="text-sm font-semibold text-[var(--site-ink)]">客服在线 · 9:00 — 23:00</span>
              </div>
            </div>

            {/* 发送邮件 */}
            <div className="flex-1 border border-[var(--site-border)] rounded-2xl p-6 sm:p-8 home-service-card bg-white">
              <div className="home-service-icon support-symbol"><Mail size={22} aria-hidden="true" /></div>
              <h3 className="text-xl font-bold text-[var(--site-ink)] mb-3">发送邮件</h3>
              <a
                href="mailto:ricky@savvyai.plus"
                className="inline-block text-base font-mono font-semibold text-[var(--site-ink)] mb-4 hover:text-[var(--site-brand)] transition-colors"
              >
                ricky@savvyai.plus
              </a>
              <p className="text-sm text-[var(--site-muted)] leading-relaxed">
                邮件回复较慢，平均 2–3 天回复一次，不适合紧急的工单咨询或需求。急事请直接使用在线客服。
              </p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
